import {
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState
} from "react";
import type { GroupMode, SortMode } from "../lib/query-models";
import { SORT_OPTIONS } from "../lib/query-models";
import { useI18n } from "../hooks/use-i18n";
import { loadChineseCopy, ui } from "../lib/localization";

interface AppHeaderProps {
  query: string;
  searchRef: RefObject<HTMLInputElement | null>;
  group: GroupMode;
  sort: SortMode;
  activeFilterCount: number;
  onQuery: (value: string) => void;
  onGroup: (value: GroupMode) => void;
  onSort: (value: SortMode) => void;
  onOpenFilters: () => void;
}

interface MenuOption<T extends string> {
  label: string;
  value: T;
}

// Warm the Simplified Chinese chunk as soon as the language button is pointed
// at or focused, so the switch itself stays instant.
function prefetchChineseCopy() {
  void loadChineseCopy().catch(() => undefined);
}

function ControlMenu<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: T;
  options: MenuOption<T>[];
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const control = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: PointerEvent) => {
      if (!control.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Focus on an option would drop to the page when the option unmounts.
      if (control.current?.contains(document.activeElement)) trigger.current?.focus();
      setOpen(false);
    };
    window.addEventListener("pointerdown", closeOnOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div
      ref={control}
      className={`control-menu${open ? " is-open" : ""}`}
      onBlur={(event) => {
        // Close once keyboard focus moves to another control; otherwise the
        // open menu stayed painted under it. A blur with no new target is a
        // click somewhere, which the outside-pointerdown listener handles,
        // and a click on the menu's own padding must not close it.
        const next = event.relatedTarget as Node | null;
        if (next && !event.currentTarget.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="control-menu__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{label}</span>
        <strong>{selected?.label ?? value}</strong>
        <i aria-hidden="true" />
      </button>
      {open ? (
        <div className="control-menu__options" id={menuId} role="listbox">
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? "is-selected" : ""}
              onClick={(event) => {
                onChange(option.value);
                setOpen(false);
                // A keyboard pick (a click with no pointer detail) keeps focus
                // on the control; a mouse pick leaves it with the page, so the
                // list's j/k/Enter keys do not reach the trigger.
                if (event.detail === 0) trigger.current?.focus();
              }}
              key={option.value}
            >
              <span className="control-menu__option-label">{option.label}</span>
              {option.value === value ? (
                <span className="control-menu__option-dot" aria-hidden="true" />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AppHeader({
  query,
  searchRef,
  group,
  sort,
  activeFilterCount,
  onQuery,
  onGroup,
  onSort,
  onOpenFilters
}: AppHeaderProps) {
  const { language, t, toggleLanguage } = useI18n();
  const groupOptions: MenuOption<GroupMode>[] = [
    { label: t("groupDate"), value: "date" },
    { label: t("groupCompany"), value: "company" },
    { label: t("groupCategory"), value: "category" },
    { label: t("groupNone"), value: "none" }
  ];
  const sortOptions = SORT_OPTIONS[group].map((option) => ({
    value: option.value,
    label: ui(
      language,
      option.value === "newest"
        ? "sortNewest"
        : option.value === "oldest"
          ? "sortOldest"
          : option.value === "latest"
            ? "sortLatestRelease"
            : option.value === "count"
              ? "sortModelCount"
              : option.value === "company"
                ? "sortCompany"
                : option.value === "category"
                  ? "sortCategory"
                  : option.value === "status"
                    ? "sortReleaseStatus"
                    : group === "company"
                      ? "sortCompanyName"
                      : group === "category"
                        ? "sortCategoryName"
                        : "sortModelName"
    )
  }));

  return (
    <header className="app-header">
      <label className="search-field">
        <input
          ref={searchRef}
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={t("searchPlaceholder")}
          aria-label={t("searchAria")}
        />
        <kbd>/</kbd>
      </label>

      <div className="header-controls">
        <ControlMenu
          label={t("group")}
          value={group}
          options={groupOptions}
          onChange={onGroup}
        />
        <ControlMenu
          label={t("sort")}
          value={sort}
          options={sortOptions}
          onChange={onSort}
        />

        <button
          className="filter-button"
          type="button"
          onClick={onOpenFilters}
          aria-label={
            activeFilterCount
              ? `${t("filters")}${language === "zh" ? "，" : ", "}${activeFilterCount} ${t("filtersActive")}`
              : t("filters")
          }
        >
          <span>{t("filters")}</span>
          {activeFilterCount ? (
            <b>
              <span>{activeFilterCount}</span>
            </b>
          ) : null}
        </button>

        <button
          className="language-button"
          type="button"
          onClick={toggleLanguage}
          onPointerEnter={prefetchChineseCopy}
          onFocus={prefetchChineseCopy}
          aria-label={
            language === "en" ? t("switchToChinese") : t("switchToEnglish")
          }
          title={
            language === "en" ? t("switchToChinese") : t("switchToEnglish")
          }
        >
          <span>{language === "en" ? "中文" : "EN"}</span>
        </button>
      </div>
    </header>
  );
}
